/**
 * FakeLink Hook
 *
 * Makes an element behave like a link while preserving interactive child elements:
 * - Clicking anywhere on the element navigates to the specified URL
 * - Clicking on interactive elements (buttons, links, inputs, etc.) prevents navigation
 * - Supports any navigation target via data-to attribute
 *
 * Usage: Add phx-hook="FakeLink" to any element
 * - Element should have data-to attribute with the navigation URL
 * - Interactive elements should have data-interactive="true" to prevent navigation
 */

import { Hook, makeHook } from './Hook'

export default makeHook(
  class FakeLink extends Hook {
    mounted() {
      this.setupClickHandler()
    }

    private setupClickHandler() {
      const onNavigate = this.el.dataset.onNavigate
      if (!onNavigate) {
        return
      }

      // Add cursor pointer to indicate clickability
      this.el.style.cursor = 'pointer'

      this.el.addEventListener('click', (event) => {
        const target = event.target as HTMLElement

        // Check if the click target or any parent has data-interactive="true"
        const interactiveElement = target.closest('[data-interactive="true"]')
        if (interactiveElement) {
          // Allow the interactive element to handle the click
          return
        }

        // Check for other interactive elements that should prevent navigation
        const preventNavigation = target.closest(`
          button,
          a,
          input,
          select,
          textarea,
          [role="button"],
          [tabindex]:not([tabindex="-1"]),
          .dropdown,
          [data-floating],
          [phx-click]
        `)

        if (preventNavigation) {
          return
        }

        // Navigate using LiveView navigation
        this.liveSocket.execJS(this.el, onNavigate)
      })
    }
  }
)
